import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownDivider,
  DropdownShortcut,
  MenubarItem
} from '@criteria-labs/react'
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  DocumentIcon,
  DocumentPlusIcon,
  FolderIcon,
  FolderPlusIcon,
  GlobeAsiaAustraliaIcon
} from '@heroicons/react/24/outline'
import { fileOpen } from 'browser-fs-access'
import { useState } from 'react'
import { downloadFile } from '../../lib/downloadFile'
import { useKeydownCommand } from '../../lib/useKeydownCommand'
import { ProjectFile, ProjectFolder } from '../ProjectItem'
import { ProjectManager } from '../ProjectManager'
import { useProjectActiveFile } from '../selection/useProjectActiveFile'
import { useProjectSelection } from '../selection/useProjectSelection'
import { ChooseTemplateDialog } from './ChooseTemplateDialog'
import { ImportURLDialog } from './ImportURLDialog'

export function FileMenu() {
  const [newFileDialogOpen, setNewFileDialogOpen] = useState(false)
  const [importURLDialogOpen, setImportURLDialogOpen] = useState(false)

  const { selection, select } = useProjectSelection()

  const activeFile = useProjectActiveFile()

  const handleNewTextFile = async () => {
    const parentFolder = ProjectManager.shared().getNearestFolder(selection)
    ProjectManager.shared().createFileSync(parentFolder, 'Untitled.yaml', '', (createdFile: ProjectFile) => {
      select(createdFile.path, true)
    })
  }

  const handleNewFile = () => setNewFileDialogOpen(true)

  const handleNewFolder = async () => {
    const parentFolder = ProjectManager.shared().getNearestFolder(selection)
    ProjectManager.shared().createFolderSync(parentFolder, 'Untitled', (createdFolder: ProjectFolder) => {
      select(createdFolder.path, true)
    })
  }

  const handleOpenFile = async () => {
    await ProjectManager.shared().openFile()
  }

  const handleAddFolderToWorkspace = async () => {
    await ProjectManager.shared().addFolderToWorkspace()
  }

  const handleSave = async () => {
    if (!activeFile) {
      return // disabled
    }
    // Doesn't actually do anything as it saves as you type
    // await ProjectManager.shared().saveFile(activeFile)
  }

  const handleImportFile = async () => {
    try {
      const file = await fileOpen({
        mimeTypes: ['text/*', 'application/json', 'application/yaml']
      })
      const text = await file.text()
      const name = file.name

      const parentFolder = ProjectManager.shared().getNearestFolder(selection)
      ProjectManager.shared().createFileSync(parentFolder, name, text, (createdFile: ProjectFile) => {
        select(createdFile.path, true)
      })
    } catch (error) {
      if (error instanceof DOMException) {
        if (error.name === 'AbortError') {
          return
        }
      }
      throw error
    }
  }

  const handleDownload = async () => {
    if (!activeFile || !activeFile.fileHandle) {
      return // disabled
    }
    const file = await activeFile.fileHandle.getFile()
    await downloadFile(file)
  }

  const newTextFileShortcut = useKeydownCommand(
    'n',
    {
      macOS: { altKey: true, metaKey: true },
      other: { ctrlKey: true }
    },
    handleNewTextFile
  )

  const newFileShortcut = useKeydownCommand(
    'n',
    {
      macOS: { altKey: true, ctrlKey: true, metaKey: true },
      other: { altKey: true, ctrlKey: true, metaKey: true }
    },
    handleNewFile
  )

  const openFileShortcut = useKeydownCommand(
    'o',
    {
      macOS: { metaKey: true },
      other: { ctrlKey: true }
    },
    handleOpenFile
  )

  const saveShortcut = useKeydownCommand(
    's',
    {
      macOS: { metaKey: true },
      other: { ctrlKey: true }
    },
    handleSave
  )

  return (
    <>
      <Dropdown>
        <DropdownButton as={MenubarItem} aria-label="File">
          File
        </DropdownButton>
        <DropdownMenu anchor="bottom start" modal={false}>
          <DropdownItem onClick={handleNewTextFile} aria-label="New Text File">
            <DocumentPlusIcon />
            <DropdownLabel>New Text File</DropdownLabel>
            <DropdownShortcut keys={newTextFileShortcut} />
          </DropdownItem>
          <DropdownItem onClick={handleNewFile} aria-label="New File">
            <DocumentPlusIcon />
            <DropdownLabel>New File&hellip;</DropdownLabel>
            <DropdownShortcut keys={newFileShortcut} />
          </DropdownItem>
          <DropdownItem onClick={handleNewFolder} aria-label="New Folder">
            <FolderPlusIcon />
            <DropdownLabel>New Folder</DropdownLabel>
          </DropdownItem>
          <DropdownDivider />
          <DropdownItem onClick={handleOpenFile} aria-label="Open File">
            <DocumentIcon />
            <DropdownLabel>Open File&hellip;</DropdownLabel>
            <DropdownShortcut keys={openFileShortcut} />
          </DropdownItem>
          {/* <DropdownItem
            onClick={async () => {
              await ProjectManager.shared().openFolder()
            }}
          >
            <FolderIcon />
            <DropdownLabel>Open Folder&hellip;</DropdownLabel>
          </DropdownItem> */}
          <DropdownItem onClick={handleAddFolderToWorkspace} aria-label="Add Folder To Workspace">
            <FolderIcon />
            <DropdownLabel>Add Folder To Workspace&hellip;</DropdownLabel>
          </DropdownItem>
          <DropdownDivider />
          <DropdownItem disabled={!activeFile} onClick={handleSave} aria-label="Save">
            <FolderIcon />
            <DropdownLabel>Save</DropdownLabel>
            <DropdownShortcut keys={saveShortcut} />
          </DropdownItem>
          <DropdownDivider />
          <DropdownItem onClick={() => setImportURLDialogOpen(true)} aria-label="Import URL">
            <GlobeAsiaAustraliaIcon />
            <DropdownLabel>Import URL</DropdownLabel>
          </DropdownItem>
          <DropdownItem onClick={handleImportFile} aria-label="Import File">
            <ArrowUpTrayIcon />
            <DropdownLabel>Import File</DropdownLabel>
          </DropdownItem>
          <DropdownDivider />
          <DropdownItem disabled={!activeFile || !activeFile.fileHandle} onClick={handleDownload} aria-label="Download">
            <ArrowDownTrayIcon />
            <DropdownLabel>Download</DropdownLabel>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
      <ImportURLDialog open={importURLDialogOpen} onClose={setImportURLDialogOpen} />
      <ChooseTemplateDialog
        open={newFileDialogOpen}
        onClose={setNewFileDialogOpen}
        onChooseTemplate={async ({ name, text }) => {
          const parentFolder = ProjectManager.shared().getNearestFolder(selection)
          ProjectManager.shared().createFileSync(parentFolder, name, text, (createdFile: ProjectFile) => {
            select(createdFile.path, true)
          })

          setNewFileDialogOpen(false)
        }}
      />
    </>
  )
}
